import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useCallback, useMemo } from 'react'
import useSWR from 'swr'

import type { LotoReportEntry, LotoReportScope } from './lotoReportSchemas'
import {
  deleteLotoReport,
  loadLotoReports,
  putLotoReport,
} from './lotoReportStore'

type LotoReportsKey = [string, 'loto-report', string, string, string]

type UseLotoReportsResult = {
  reports: LotoReportEntry[]
  error: Error | undefined
  isLoading: boolean
  save: (entry: LotoReportEntry) => Promise<void>
  remove: (entry: LotoReportEntry) => Promise<void>
  refresh: () => Promise<LotoReportEntry[] | undefined>
}

type LotoReportAction = typeof putLotoReport

function requireScope(scope: LotoReportScope | undefined): LotoReportScope {
  if (!scope) throw new Error('LOTO report scope is not available')
  return scope
}

export function useLotoReports(
  scope: LotoReportScope | undefined,
  enabled = true
): UseLotoReportsResult {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const endpoint = `${ROLODEX_URL}/api/v2/entries`
  const organization = scope?.organization
  const site = scope?.site
  const pipelineConfig = scope?.pipelineConfig
  const currentScope = useMemo(
    () =>
      organization && site && pipelineConfig
        ? { organization, site, pipelineConfig }
        : undefined,
    [organization, site, pipelineConfig]
  )
  const key: LotoReportsKey | null =
    enabled && isAuthenticated && currentScope
      ? [
          endpoint,
          'loto-report',
          currentScope.organization,
          currentScope.site,
          currentScope.pipelineConfig,
        ]
      : null
  const { data, error, isLoading, mutate } = useSWR<
    LotoReportEntry[],
    Error,
    LotoReportsKey | null
  >(key, ([url]) =>
    loadLotoReports(authorizedAxios, url, requireScope(currentScope))
  )
  const run = useCallback(
    async (action: LotoReportAction, entry: LotoReportEntry): Promise<void> => {
      await action(authorizedAxios, endpoint, requireScope(currentScope), entry)
      await mutate()
    },
    [authorizedAxios, endpoint, mutate, currentScope]
  )
  const save = useCallback(
    (entry: LotoReportEntry) => run(putLotoReport, entry),
    [run]
  )
  const remove = useCallback(
    (entry: LotoReportEntry) => run(deleteLotoReport, entry),
    [run]
  )
  return {
    reports: data ?? [],
    error,
    isLoading,
    save,
    remove,
    refresh: mutate,
  }
}
